/* CRUX newsletter no-refresh popup v1 */
(() => {
  'use strict';

  const dialog = document.querySelector('[data-crux-newsletter-dialog]');
  if (!(dialog instanceof HTMLElement)) return;

  const closeButtons = Array.from(dialog.querySelectorAll('[data-crux-newsletter-close]'));
  const supportsNativeDialog = typeof dialog.showModal === 'function' && typeof dialog.close === 'function';
  let returnFocus = null;

  const isOpen = () => supportsNativeDialog ? dialog.open : dialog.hasAttribute('open');

  const restoreFocus = () => {
    document.documentElement.classList.remove('crux-newsletter-dialog-open');
    if (returnFocus instanceof HTMLElement && document.documentElement.contains(returnFocus)) {
      returnFocus.focus();
    }
    returnFocus = null;
  };

  const closeDialog = () => {
    if (!isOpen()) return;
    if (supportsNativeDialog) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      dialog.removeAttribute('aria-modal');
      restoreFocus();
    }
  };

  const openSuccess = (trigger) => {
    returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
    if (!isOpen()) {
      if (supportsNativeDialog) {
        dialog.showModal();
      } else {
        dialog.classList.add('crux-newsletter-dialog--fallback');
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
        dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('crux-newsletter-dialog-open');
    }
    requestAnimationFrame(() => closeButtons[0]?.focus());
  };

  closeButtons.forEach((button) => button.addEventListener('click', closeDialog));
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeDialog();
  });
  dialog.addEventListener('close', restoreFocus);
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !supportsNativeDialog) {
      event.preventDefault();
      closeDialog();
      return;
    }

    if (event.key !== 'Tab' || supportsNativeDialog) return;
    const focusable = Array.from(dialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled])'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const isFooterNewsletterForm = (form) => {
    if (!(form instanceof HTMLFormElement) || !form.closest('footer')) return false;
    const email = form.querySelector('input[type="email"][name="contact[email]"]');
    if (!(email instanceof HTMLInputElement)) return false;

    try {
      const action = new URL(form.action, window.location.href);
      return action.origin === window.location.origin && action.pathname === '/contact';
    } catch (_error) {
      return false;
    }
  };

  const getMessage = (form) => {
    const block = form.closest('.email-signup-block') || form.parentElement;
    let message = block?.querySelector('[data-crux-newsletter-message]');
    if (!(message instanceof HTMLElement) && block) {
      message = document.createElement('p');
      message.className = 'crux-newsletter-field-message';
      message.dataset.cruxNewsletterMessage = '';
      message.setAttribute('role', 'alert');
      block.append(message);
    }
    return message instanceof HTMLElement ? message : null;
  };

  const clearMessage = (form) => {
    const message = getMessage(form);
    if (message) {
      message.textContent = '';
      message.hidden = true;
    }
  };

  const showMessage = (form, text) => {
    const message = getMessage(form);
    if (message) {
      message.textContent = text;
      message.hidden = false;
    }
  };

  const extractResponseError = (html, formId) => {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const scope = formId ? parsed.getElementById(formId) : null;
    const error = (scope || parsed).querySelector('.errors, [role="alert"], .form-status--error, .form__message');
    return error?.textContent?.replace(/\s+/g, ' ').trim() || '';
  };

  const responseSucceeded = (response, html, formId) => {
    try {
      const responseUrl = new URL(response.url, window.location.href);
      if (responseUrl.searchParams.get('customer_posted') === 'true') return true;
    } catch (_error) {
      // Continue with the returned markup when a response URL is unavailable.
    }

    const parsed = new DOMParser().parseFromString(html || '', 'text/html');
    const scope = formId ? parsed.getElementById(formId) : null;
    const success = (scope || parsed).querySelector(
      '.form-status--success, .email-signup__message--success, [data-form-status="success"]'
    );
    const error = (scope || parsed).querySelector('.errors, [role="alert"], .form-status--error');
    return response.ok && Boolean(success) && !error;
  };

  const handleSubmit = async (event) => {
    const form = event.target;
    if (!isFooterNewsletterForm(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const email = form.querySelector('input[type="email"][name="contact[email]"]');
    const submit = form.querySelector('button[type="submit"], button:not([type]), input[type="submit"]');
    if (!(email instanceof HTMLInputElement) || !(submit instanceof HTMLElement)) return;
    if (form.dataset.cruxNewsletterBusy === 'true') return;

    clearMessage(form);
    email.removeAttribute('aria-invalid');

    if (!email.checkValidity()) {
      email.setAttribute('aria-invalid', 'true');
      showMessage(form, dialog.dataset.invalidEmail || 'Enter a valid email address.');
      email.focus();
      return;
    }

    const originalMarkup = submit.innerHTML;
    const originalDisabled = submit.hasAttribute('disabled');
    form.dataset.cruxNewsletterBusy = 'true';
    submit.setAttribute('disabled', '');
    submit.setAttribute('aria-busy', 'true');
    submit.textContent = dialog.dataset.submitting || 'Joining...';

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        redirect: 'follow',
      });
      const html = await response.text();

      if (!responseSucceeded(response, html, form.id)) {
        throw new Error(extractResponseError(html, form.id));
      }

      form.reset();
      clearMessage(form);
      openSuccess(submit);
    } catch (error) {
      const responseMessage = error instanceof Error ? error.message : '';
      showMessage(form, responseMessage || dialog.dataset.submitError || 'Please try again.');
    } finally {
      delete form.dataset.cruxNewsletterBusy;
      submit.removeAttribute('aria-busy');
      submit.innerHTML = originalMarkup;
      if (!originalDisabled) submit.removeAttribute('disabled');
    }
  };

  document.addEventListener('submit', handleSubmit, true);

  window.CruxNewsletterPopup = Object.freeze({ openSuccess, close: closeDialog });
})();
